import fp from 'lodash/fp';
import React, { useState, useCallback, useEffect } from 'react';
import { formatDate, formatCurrency, formatOr, formatDateTime } from '@factorin/util';

import { connect } from 'Modules/core/redux';
import { Text, Button, cn, SortArrow, useMemoDeps } from 'Modules/ui';
import { translate } from 'Modules/core/i18n';
import {
    PaginationLoader,
    paginationActions,
    getDomainTypeFromPath,
} from 'Modules/business/data';

import { Dock, useDock } from '../Dock';
import { FactorinGrid } from '../FactorinGrid';
import styles from './index.module.scss';


export const columnTypes = {
    STRING: 'STRING',
    DATE: 'DATE',
    DATETIME: 'DATETIME',
    CURRENCY: 'CURRENCY',
    COMPOSITE: 'COMPOSITE',
};

export const fappViewColumnRenderers = {
    [columnTypes.STRING]: ({ column: { field }, data }) => fp.get(field, data),
    [columnTypes.DATE]: ({ column: { field }, data }) => formatDate(fp.get(field, data)),
    [columnTypes.DATETIME]: ({ column: { field }, data }) => formatDateTime(fp.get(field, data)),
    [columnTypes.CURRENCY]: ({ column: { field }, data }) =>
        formatOr(fp.get(field, data), value => `${formatCurrency(value)} ₽`),
    [columnTypes.COMPOSITE]: translate('domains')(({
        t, column: { field, secondaryField }, data,
    }) => {
        const { key, values } = secondaryField(data);

        return (
            <div className={styles.compositeCell}>
                <Text component='div' textType='light' textCompact ellipsis>
                    {fp.get(field, data)}
                </Text>
                {key && (
                    <Text
                        component='div'
                        textType='info'
                        textCompact
                        ellipsis
                        className={styles.second}
                    >
                        {t(`utils.${key}`, values)}
                    </Text>
                )}
            </div>
        );
    }),
};

const selectedValues = ({ all, include, exclude }, total) => ({
    checkedCount: all ? total - exclude.length : include.length,
});

const allSelected = ({ all, exclude }) => all && fp.isEmpty(exclude);

const SortButton = fp.compose(
    translate('domains'),
    connect(
        ({ data: { pagination } }, { column: { path } }) => ({
            parameters: (pagination[path] || {}).parameters,
        }),
        (dispatch, { column: { path } }) => ({
            setParameters: parameters => dispatch(
                paginationActions.setParameters(path, parameters),
            ),
        }),
    ),
)(({ t, column: { domainType, field, sortField }, parameters = {}, setParameters }) => {
    const sortingField = sortField || field;
    return (
        <Button
            variant='auxiliary'
            className={cn(
                styles.sortButton,
                parameters.sorting === sortingField && (
                    parameters.descending ? styles.desc : styles.asc
                ),
            )}
            onClick={useCallback(() => setParameters({
                ...parameters,
                sorting: parameters.sorting === sortingField && parameters.descending
                    ? undefined
                    : sortingField,
                descending: parameters.sorting === sortingField && !parameters.descending,
            }), [sortingField, parameters, setParameters])}
        >
            <span>
                {t(`${domainType}.columns.${field}`)}
            </span>
            <SortArrow />
        </Button>
    );
});

const SimpleHeader = translate('domains')(
    ({ t, column: { domainType, field } }) =>
        t(`${domainType}.columns.${field}`),
);

const mapColumns = (path, columns, CustomSortButton, viewColumnRenderers = {}) => columns.map(({
    type,
    sortable = true,
    Header,
    Cell,
    ...column
}) => ({
    ...column,
    path,
    domainType: getDomainTypeFromPath(path),
    Header: Header || (sortable ? CustomSortButton || SortButton : SimpleHeader),
    Cell: Cell || { ...fappViewColumnRenderers, ...viewColumnRenderers }[type],
}));

const FactorinViewComponent = ({
    t, name,
    dock: DockComponent,
    buttons: Buttons,
    viewColumnRenderers,
    path,
    storage,
    paginationElements,
    paginationTotal,
    columns,
    checkable,
    checkedRows,
    checkedTotal,
    classNames = {},
    onRowClick: onRowClickCallback,
    rowSelectable,
    customSortButton,
    needFirstRowSelect = false,
    userSetVScrollBarVisible,
    ...props
}) => {
    const [{ data, selectedRow }, setSelected] = useState({});

    const { openDock, ...dockProps } = useDock();
    const onRowClick = useCallback(
        data => {
            setSelected({
                data: data.rowData,
                selectedRow: data.rowNumber,
            });

            if (DockComponent)
                openDock();

            if (onRowClickCallback)
                onRowClickCallback(data);
        },
        [DockComponent, onRowClickCallback, openDock],
    );

    const mappedColumns =
        useMemoDeps(mapColumns, [path, columns, customSortButton, viewColumnRenderers]);

    const isEmpty = !fp.isNumber(paginationTotal);

    useEffect(() => {
        if (needFirstRowSelect && !selectedRow && paginationTotal > 0) {
            setSelected({
                data: paginationElements[0],
                selectedRow: 0,
            });
        }
    }, [needFirstRowSelect, paginationElements, paginationTotal, selectedRow]);

    return (
        <div className={cn(styles.factorinView, classNames.container)}>
            {name && (
                <div className={cn(styles.header, classNames.header)}>
                    <div>
                        <div className={styles.title}>
                            {name}
                            <Text component='div' textType='info' className={styles.info}>
                                {checkable && !isEmpty
                                    ? allSelected(checkedRows)
                                        ? t('allSelected')
                                        : t('selected', selectedValues(
                                            checkedRows,
                                            checkedTotal || paginationTotal,
                                        ))
                                    : !isEmpty
                                        ? t('all', { total: paginationTotal })
                                        : t('allUnknown')
                                }
                            </Text>
                        </div>
                        {Buttons && (
                            <div className={cn(styles.buttons, classNames.buttons)}>
                                <Buttons checkedRows={checkedRows} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <PaginationLoader
                path={path}
                userSetVScrollBarVisible={userSetVScrollBarVisible}
                container={FactorinGrid}
                getData={useCallback(
                    index => storage[paginationElements[index]],
                    [paginationElements, storage],
                )}
                selectedRow={selectedRow}
                onRowClick={rowSelectable || DockComponent ? onRowClick : undefined}
                columns={mappedColumns}
                checkable={checkable}
                checkedRows={checkedRows}
                classNames={classNames.grid}
                {...props}
            />

            {DockComponent && data && (
                <Dock {...dockProps}>
                    <DockComponent data={data} />
                </Dock>
            )}
        </div>
    );
};


export const FactorinView = fp.compose(
    React.memo,
    translate('factorinView'),
    connect(
        ({ data }, { path }) => ({
            paginationElements: fp.get(`pagination[${path}].elements`, data),
            paginationTotal: fp.get(`pagination[${path}].total`, data),
            storage: data.storage[getDomainTypeFromPath(path)],
        }),
    ),
)(FactorinViewComponent);
